import i18n from '@/utils/i18n';
import {
  orderEntryCollection,
  orderGuideItemCollection,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/order-entry-collection';
import { useCallback } from 'react';
import { useUpdateOrderEntryItemRequestNoteMutation } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/documents/update-order-entry-item-request-note.generated';
import type { RequestNoteSavePayload } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';

interface UseRequestNoteMutatorParams {
  onSaveError: (message: string) => void;
  restaurantUUID: string;
  supplierUUID: string;
}

/**
 * Mutator for saving request notes on order entry items.
 *
 * Optimistically updates both `orderEntryCollection` (transformed table
 * data) and `orderGuideItemCollection` (raw GraphQL data), then fires
 * the `updateOrderEntryItemRequestNote` mutation. Rolls back both
 * collections on failure.
 *
 * @param params.onSaveError - Callback invoked with an error message on mutation failure
 * @param params.restaurantUUID - The restaurant to save the note for
 * @param params.supplierUUID - The supplier context
 * @returns `commitNote` — async function that persists a single request note
 */
export const useRequestNoteMutator = ({
  onSaveError,
  restaurantUUID,
  supplierUUID,
}: UseRequestNoteMutatorParams) => {
  const [updateRequestNote] = useUpdateOrderEntryItemRequestNoteMutation();

  const commitNote = useCallback(
    async (payload: RequestNoteSavePayload) => {
      const { collectionId, persistRequest, request, variantPackUUID } =
        payload;

      const updatedNote = {
        collectionId,
        persistRequest,
        request: request || null,
      };

      // Snapshot pre-mutation state for rollback
      const previousTableItem = orderEntryCollection.get(variantPackUUID);
      const previousOrderGuideItem =
        orderGuideItemCollection.get(variantPackUUID);

      // Optimistically update both collections so the table reflects
      // the change immediately while the mutation is in flight.
      if (previousTableItem) {
        orderEntryCollection.update(variantPackUUID, (draft) => {
          const idx = draft.requestNotes.findIndex(
            (n) => n.collectionId === collectionId,
          );
          if (idx >= 0) {
            draft.requestNotes[idx] = updatedNote;
          } else {
            draft.requestNotes = [...draft.requestNotes, updatedNote];
          }
        });
      }

      if (previousOrderGuideItem) {
        orderGuideItemCollection.update(variantPackUUID, (draft) => {
          const notes = draft.request_notes ?? [];
          const idx = notes.findIndex((n) => n.collection_id === collectionId);
          const rawNote = {
            collection_id: collectionId,
            persist_request: persistRequest,
            request: request || null,
          };
          if (idx >= 0) {
            notes[idx] = rawNote;
          } else {
            draft.request_notes = [...notes, rawNote];
          }
        });
      }

      try {
        await updateRequestNote({
          variables: {
            collectionId,
            persistRequest,
            request: request || null,
            restaurantId: restaurantUUID,
            supplierId: supplierUUID,
            variantPackId: variantPackUUID,
          },
        });
      } catch (error) {
        // Rollback: restore the pre-mutation snapshots
        if (previousTableItem) {
          orderEntryCollection.delete([variantPackUUID]);
          orderEntryCollection.insert([previousTableItem]);
        }
        if (previousOrderGuideItem) {
          orderGuideItemCollection.delete([variantPackUUID]);
          orderGuideItemCollection.insert([previousOrderGuideItem]);
        }

        onSaveError(i18n.t('sales_portal.order_entry.table.notes_save_failed'));

        // Re-throw so callers (e.g. the cell) can revert optimistic UI state.
        throw error;
      }
    },
    [onSaveError, restaurantUUID, supplierUUID, updateRequestNote],
  );

  return { commitNote };
};
